
"use client";

import { createContext, useContext, useEffect, useState, type ReactNode, useCallback } from 'react';
import { type Offer, offers as fallbackOffers } from '@/lib/data';
import { db } from '@/lib/firebase';
import { collection, getDocs, addDoc, doc, updateDoc, deleteDoc, query, orderBy, writeBatch } from 'firebase/firestore';
import { useToast } from '@/hooks/use-toast';

interface OfferContextType {
  offers: Offer[];
  addOffer: (offer: Omit<Offer, 'id'>) => Promise<void>;
  updateOffer: (id: string, updatedOffer: Partial<Omit<Offer, 'id'>>) => Promise<void>;
  deleteOffer: (id: string) => Promise<void>;
  loading: boolean;
  refreshOffers: () => void;
}

const OfferContext = createContext<OfferContextType | undefined>(undefined);

export const OfferProvider = ({ children }: { children: ReactNode }) => {
  const [offers, setOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchOffers = useCallback(async () => {
    setLoading(true);
    try {
      const offersCollection = collection(db, 'offers');
      const q = query(offersCollection, orderBy('payout', 'desc'));
      const snapshot = await getDocs(q);

      const offerList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
      } as Offer));
      setOffers(offerList);

    } catch (error) {
      console.error("Failed to fetch offers from Firestore", error);
      toast({ variant: 'destructive', title: "Error", description: "Could not load offers from the database." });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  const addOffer = async (offerData: Omit<Offer, 'id'>) => {
    try {
      const offersCollection = collection(db, 'offers');
      const docRef = await addDoc(offersCollection, offerData);
      setOffers(prev => [...prev, {...offerData, id: docRef.id}].sort((a,b) => b.payout - a.payout));
    } catch (error) {
      console.error("Error adding offer:", error);
      toast({ variant: 'destructive', title: "Error", description: "Could not add the offer." });
      throw error;
    }
  };

  const updateOffer = async (id: string, updatedOfferData: Partial<Omit<Offer, 'id'>>) => {
     try {
       if (Object.keys(updatedOfferData).length === 0) {
           toast({ title: "No changes to save." });
           return;
       }

       const offerDocRef = doc(db, 'offers', id);
       await updateDoc(offerDocRef, updatedOfferData);
       
       setOffers(prev =>
         prev.map(offer => (offer.id === id ? { ...offer, ...updatedOfferData } : offer)).sort((a,b) => b.payout - a.payout)
       );
     } catch (error) {
       console.error("Error updating offer:", error);
       toast({ variant: 'destructive', title: "Error", description: "Could not update the offer." });
       throw error;
     }
  };

  const deleteOffer = async (id: string) => {
    try {
      const offerDocRef = doc(db, 'offers', id);
      await deleteDoc(offerDocRef);
      setOffers(prev => prev.filter(offer => offer.id !== id));
    } catch (error) {
       console.error("Error deleting offer:", error);
       toast({ variant: 'destructive', title: "Error", description: "Could not delete the offer." });
       throw error;
    }
  };

  return (
    <OfferContext.Provider value={{ offers, addOffer, updateOffer, deleteOffer, loading, refreshOffers: fetchOffers }}>
      {children}
    </OfferContext.Provider>
  );
};

export const useOffers = () => {
  const context = useContext(OfferContext);
  if (context === undefined) {
    throw new Error('useOffers must be used within an OfferProvider');
  }
  return context;
};
